import Konva from 'konva';
import type { ComponentDef } from '../../core/library/component-def.js';

/**
 * Renderer del cuerpo de un display de 7 segmentos (1 dígito, tipo
 * 5161AH). Se ve DESDE ARRIBA como en la referencia:
 *   - marco rojo oscuro / marrón (color del encapsulado),
 *   - ventana negra grande que ocupa la mayor parte del frontal,
 *   - dentro, los 7 segmentos + el punto decimal apagados (silueta
 *     tenue), formando el clásico "8." apenas visible.
 *
 * Invertido (`flipped=true`) se muestra sólo el cuerpo plano: no hay
 * ventana ni segmentos porque esa cara es la trasera del encapsulado.
 */
export function renderSevenSegmentBody(
  def: ComponentDef,
  _labelText: string,
  _rotationDeg: number = 0,
  flipped: boolean = false,
): Konva.Group {
  const g = new Konva.Group();

  const w = def.body.length_mm ?? 12.7;
  const h = def.body.width_mm ?? 20.32;
  const cr = def.body.corner_radius_mm ?? 0.4;
  const bodyColor = def.appearance.body_color ?? '#3b1414';

  // Cuerpo (encapsulado) con degradado sutil.
  g.add(new Konva.Rect({
    x: -w / 2, y: -h / 2, width: w, height: h,
    cornerRadius: cr,
    fillLinearGradientStartPoint: { x: 0, y: -h / 2 },
    fillLinearGradientEndPoint: { x: 0, y: h / 2 },
    fillLinearGradientColorStops: [
      0, mix(bodyColor, '#ffffff', 0.10),
      1, mix(bodyColor, '#000000', 0.15),
    ],
    stroke: '#000000',
    strokeWidth: 0.15,
    listening: true,
  })).setAttr('nodeKind', 'body');

  if (flipped) {
    return g;
  }

  // Cara frontal del display: ocupa TODO el cuerpo (hasta los bordes,
  // con el mismo cornerRadius que el encapsulado). Gris medio, un poco
  // más oscuro que la primera versión.
  const winW = w;
  const winH = h;
  g.add(new Konva.Rect({
    x: -winW / 2, y: -winH / 2, width: winW, height: winH,
    cornerRadius: cr,
    fill: '#333333',
    stroke: '#00000060',
    strokeWidth: 0.1,
    listening: false,
  }));

  // Geometría del dígito "8" — segmentos RECTOS (sin inclinación,
  // rectángulos ortogonales) y algo más pequeños que en la primera
  // versión para dejar aire alrededor. El bloque "dígito + punto
  // decimal" se centra COMO CONJUNTO en la ventana.
  const digitH = winH * 0.72;
  const digitW = digitH * 0.62;
  const segT = Math.min(digitW, digitH) * 0.14; // grosor de segmento
  const off = segT * 0.25;                       // separación entre segmentos
  const dpGap = segT * 0.6;                      // aire entre dígito y DP
  const dpSize = segT * 1.15;                    // lado del cuadrado del DP
  // Centro del CONJUNTO dígito+DP en x=0.
  const totalW = digitW + dpGap + dpSize;
  const cx = -totalW / 2 + digitW / 2;   // centro X del dígito
  const cy = 0;                           // centro Y del dígito

  // Coordenadas útiles del bounding-box del dígito.
  const xL = cx - digitW / 2;
  const xR = cx + digitW / 2;
  const yT = cy - digitH / 2;
  const yM = cy;
  const yB = cy + digitH / 2;

  // Color de segmento — rojo brillante, como en las hojas de datos.
  const segOn = '#e6321e';
  // Color de las trazas — negro.
  const trace = '#1a1a1a';
  const traceW = segT * 0.16;

  // Pistas pin → segmento, en forma de L (vertical + horizontal). Van
  // DEBAJO de los segmentos para que éstos las cubran limpiamente en
  // sus zonas.
  //
  // La leyenda del display (mismo pinout que el 5161AH de la foto):
  //   1→e   2→d   3→GND   4→c   5→dp
  //   6→b   7→a   8→GND   9→f  10→g
  // Los pines 3 y 8 son los COMUNES (cátodo/ánodo común, sin línea).
  //
  // Cada pin nace en el borde vertical más cercano de la ventana
  // (`yTopIn` / `yBotIn`), avanza en vertical hasta la altura del
  // segmento y remata en horizontal contra el mismo.
  const yTopIn = -winH / 2 + traceW * 0.6;
  const yBotIn =  winH / 2 - traceW * 0.6;
  // Puntos de anclaje (dentro del segmento) donde se apoya la traza.
  const anchors: Record<string, { x: number; y: number }> = {
    a:  { x: (xL + xR) / 2, y: yT + segT / 2 },
    b:  { x: xR - segT / 2, y: (yT + yM) / 2 },
    c:  { x: xR - segT / 2, y: (yM + yB) / 2 },
    d:  { x: (xL + xR) / 2, y: yB - segT / 2 },
    e:  { x: xL + segT / 2, y: (yM + yB) / 2 },
    f:  { x: xL + segT / 2, y: (yT + yM) / 2 },
    g:  { x: (xL + xR) / 2, y: yM },
    dp: { x: 0, y: 0 }, // se rellena tras calcular dpX/dpY
  };

  // xPin de cada pin (5 arriba, 5 abajo).
  const pinX = [-5.08, -2.54, 0, 2.54, 5.08];
  // Coordenada X REAL del pin dentro del componente = attachment.x
  // del PinDef; usamos el orden del JSON (1..10). Definimos qué
  // segmento va con qué pin ("" = común, sin traza).
  //   Abajo:  pin1=x[-5.08]/e, 2=[-2.54]/d, 3=[0]/GND, 4=[2.54]/c, 5=[5.08]/dp
  //   Arriba: pin6=x[5.08]/b, 7=[2.54]/a, 8=[0]/GND, 9=[-2.54]/f, 10=[-5.08]/g
  type Route = { from: 'top' | 'bot'; x: number; seg: string };
  const routes: Route[] = [
    { from: 'bot', x: pinX[0]!, seg: 'e' },
    { from: 'bot', x: pinX[1]!, seg: 'd' },
    { from: 'bot', x: pinX[3]!, seg: 'c' },
    { from: 'bot', x: pinX[4]!, seg: 'dp' },
    { from: 'top', x: pinX[4]!, seg: 'b' },
    { from: 'top', x: pinX[3]!, seg: 'a' },
    { from: 'top', x: pinX[1]!, seg: 'f' },
    { from: 'top', x: pinX[0]!, seg: 'g' },
  ];

  // Punto decimal — a la derecha del dígito, alineado con la base.
  const dpR = dpSize / 2;
  const dpX = xR + dpGap + dpR;
  const dpY = yB - dpR;
  anchors['dp'] = { x: dpX, y: dpY };

  for (const r of routes) {
    const anc = anchors[r.seg];
    if (!anc) continue;
    const yStart = r.from === 'top' ? yTopIn : yBotIn;
    // L en dos tramos: vertical desde el borde hasta anc.y y horizontal
    // hasta anc.x. Se dibuja como una sola polilínea con codo, con
    // lineJoin round para que la esquina no quede afilada.
    g.add(new Konva.Line({
      points: [ r.x, yStart, r.x, anc.y, anc.x, anc.y ],
      stroke: trace,
      strokeWidth: traceW,
      lineJoin: 'round',
      lineCap: 'round',
      listening: false,
    }));
  }

  // Segmentos ORTOGONALES: rectángulos con esquinas suavemente
  // redondeadas. Los horizontales se pintan como una barra a lo ancho
  // del dígito, los verticales como una barra a lo alto de media
  // mitad. `off` deja un pequeño hueco entre segmentos para que se
  // distingan como piezas separadas.
  const segR = segT * 0.2;    // radio de esquina, sutil
  const addRect = (x: number, y: number, ww: number, hh: number) => g.add(new Konva.Rect({
    x, y, width: ww, height: hh, cornerRadius: segR,
    fill: segOn, listening: false,
  }));

  // Horizontales (a, d, g). El ancho de los tramos rectos entre los
  // dos verticales laterales es `digitW - 2*segT - 2*off`.
  const hBarX = xL + segT + off;
  const hBarW = digitW - 2 * segT - 2 * off;
  addRect(hBarX, yT, hBarW, segT);                       // a
  addRect(hBarX, yB - segT, hBarW, segT);                // d
  addRect(hBarX, yM - segT / 2, hBarW, segT);            // g

  // Verticales — la altura de cada mitad va desde debajo del segmento
  // horizontal superior hasta arriba del segmento medio, y análogamente
  // para la mitad inferior.
  const vHalfH = (digitH - 3 * segT) / 2 - 2 * off;
  const vYTop = yT + segT + off;                          // start superior
  const vYBot = yM + segT / 2 + off;                      // start inferior
  addRect(xL, vYTop, segT, vHalfH);                       // f
  addRect(xL, vYBot, segT, vHalfH);                       // e
  addRect(xR - segT, vYTop, segT, vHalfH);                // b
  addRect(xR - segT, vYBot, segT, vHalfH);                // c

  // Punto decimal a la derecha, tamaño ≈ segT (dpX/dpY calculados
  // antes para anclar la traza azul).
  g.add(new Konva.Rect({
    x: dpX - dpR, y: dpY - dpR,
    width: dpR * 2, height: dpR * 2,
    cornerRadius: dpR * 0.4,
    fill: segOn,
    listening: false,
  }));

  return g;
}

function mix(a: string, b: string, t: number): string {
  const pa = hex(a), pb = hex(b);
  const r = Math.round(pa[0] + (pb[0] - pa[0]) * t);
  const g = Math.round(pa[1] + (pb[1] - pa[1]) * t);
  const bl = Math.round(pa[2] + (pb[2] - pa[2]) * t);
  return `#${((1 << 24) + (r << 16) + (g << 8) + bl).toString(16).slice(1)}`;
}
function hex(h: string): [number, number, number] {
  const s = h.replace('#', '');
  const v = parseInt(s.length === 3 ? s.split('').map((c) => c + c).join('') : s, 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}
